// Single source of truth for "what languages exist and what code does each
// surface want".
//
// Before this file the same three languages were re-declared in five selector
// arrays (three different shapes, "Dutch" in one place and "Nederlands" in
// another) and the mapping from the app language to a locale code was rewritten
// as a local ternary in ~35 files. That duplication was not cosmetic — it is
// what produced the shipped bugs:
//   • mirror-chat indexed its label map with the wrong case, so EN/NL users read
//     Portuguese headers (fixed 28/07);
//   • Focus and BabyGrowth still map PT-BR to "pt-PT", the exact drift Finance
//     and Settings each had to fix on their own after it reached production.
//
// Rule from here on: a language is described ONCE, in LANGUAGES below, and every
// consumer asks for the code it needs. Adding a language is one row plus one
// locale file — the Record<Lang, …> maps elsewhere then fail to compile until
// they are updated, which is the point.

/** The app language, as persisted in localStorage and user_profiles.language. */
export type Lang = "EN" | "NL" | "PT-BR" | "ES" | "FR" | "IT" | "DE" | "PL" | "BG";

/** ISO-639-1, used for asset filenames, Whisper hints and short edge payloads. */
export type LangShort = "en" | "nl" | "pt" | "es" | "fr" | "it" | "de" | "pl" | "bg";

/**
 * The code the edge functions speak. Note it is NOT the BCP-47 tag: Portuguese
 * is the only one carrying a region — the *-extract / *-react family and
 * _shared/datetime-natural.ts key on "pt-BR" and on the short code for the rest.
 */
export type EdgeLocale = "en" | "nl" | "pt-BR" | "es" | "fr" | "it" | "de" | "pl" | "bg";

export interface LanguageDef {
  /** Canonical app code. */
  code: Lang;
  /** Language name in its own language — never translated. */
  nativeLabel: string;
  /** Two-letter badge for compact triggers ("PT"). */
  shortLabel: string;
  /** Regional-indicator flag emoji. */
  flag: string;
  /** ISO-639-1 code. */
  short: LangShort;
  /**
   * BCP-47 tag for Intl / toLocale* / SpeechRecognition. This is the field that
   * must never be hand-written at a call site again.
   */
  localeTag: string;
  /** Code accepted by the edge functions. */
  edgeLocale: EdgeLocale;
  /**
   * Nome do arquivo de dicionário em `src/i18n/locales/`, SEM extensão.
   *
   * É um quarto vocabulário, e não dá para derivar dos outros três: o
   * português e o espanhol usam o tag regional ("pt-BR", "es-ES") e os outros
   * sete usam o ISO-639-1. Antes do code-splitting isso vivia como nove
   * `import` escritos à mão em translations.ts; agora o carregador precisa
   * montar o caminho a partir do CÓDIGO do idioma, em runtime, então o nome
   * do arquivo passa a ser um dado da tabela — e idioma novo sem dicionário
   * não compila.
   */
  dictFile: string;
}

/**
 * Display order for every selector in the app. Settings, Welcome and Landing
 * already agreed on this order; the two onboarding screens did not (they listed
 * NL second and called it "Dutch"). They follow this now.
 */
export const LANGUAGES: readonly LanguageDef[] = [
  {
    code: "EN",
    nativeLabel: "English",
    shortLabel: "EN",
    flag: "\u{1F1EC}\u{1F1E7}",
    short: "en",
    localeTag: "en-US",
    edgeLocale: "en",
    dictFile: "en",
  },
  {
    code: "PT-BR",
    nativeLabel: "Português",
    shortLabel: "PT",
    flag: "\u{1F1E7}\u{1F1F7}",
    short: "pt",
    localeTag: "pt-BR",
    edgeLocale: "pt-BR",
    dictFile: "pt-BR",
  },
  {
    code: "NL",
    nativeLabel: "Nederlands",
    shortLabel: "NL",
    flag: "\u{1F1F3}\u{1F1F1}",
    short: "nl",
    localeTag: "nl-NL",
    edgeLocale: "nl",
    dictFile: "nl",
  },
  {
    // es-ES (Espanha), NÃO es-419. Decisão nº 2: vozes, templates e formatação
    // de Espanha. A diferença é audível e visível — um espanhol de Madri
    // identifica sotaque mexicano na primeira frase.
    code: "ES",
    nativeLabel: "Español",
    shortLabel: "ES",
    flag: "\u{1F1EA}\u{1F1F8}",
    short: "es",
    localeTag: "es-ES",
    edgeLocale: "es",
    dictFile: "es-ES",
  },
  {
    code: "FR",
    nativeLabel: "Français",
    shortLabel: "FR",
    flag: "\u{1F1EB}\u{1F1F7}",
    short: "fr",
    localeTag: "fr-FR",
    edgeLocale: "fr",
    dictFile: "fr",
  },
  {
    code: "IT",
    nativeLabel: "Italiano",
    shortLabel: "IT",
    flag: "\u{1F1EE}\u{1F1F9}",
    short: "it",
    localeTag: "it-IT",
    edgeLocale: "it",
    dictFile: "it",
  },
  {
    code: "DE",
    nativeLabel: "Deutsch",
    shortLabel: "DE",
    flag: "\u{1F1E9}\u{1F1EA}",
    short: "de",
    localeTag: "de-DE",
    edgeLocale: "de",
    dictFile: "de",
  },
  {
    code: "PL",
    nativeLabel: "Polski",
    shortLabel: "PL",
    flag: "\u{1F1F5}\u{1F1F1}",
    short: "pl",
    localeTag: "pl-PL",
    edgeLocale: "pl",
    dictFile: "pl",
  },
  {
    code: "BG",
    nativeLabel: "Български",
    shortLabel: "BG",
    flag: "\u{1F1E7}\u{1F1EC}",
    short: "bg",
    localeTag: "bg-BG",
    edgeLocale: "bg",
    dictFile: "bg",
  },
] as const;

/**
 * Launch market. Used when nothing else is known — an unauthenticated visitor
 * whose browser advertises a language we do not ship, or a stored value that
 * failed validation.
 */
export const DEFAULT_LANG: Lang = "PT-BR";

/** Languages with maintained review copy; Bulgarian remains selectable with fallback. */
export type ActiveAppLanguage = Exclude<Lang, "BG">;
export type ActiveLangShort = Exclude<LangShort, "bg">;
export const ACTIVE_LANGUAGES = LANGUAGES.filter(
  (def): def is LanguageDef & { code: ActiveAppLanguage } => def.code !== "BG",
);
export const BASE_APP_LANGUAGE: ActiveAppLanguage = "EN";
export function activeAppLanguageOf(lang: Lang | undefined): ActiveAppLanguage {
  return ACTIVE_LANGUAGES.find(def => def.code === lang)?.code ?? BASE_APP_LANGUAGE;
}

const BY_CODE: Record<Lang, LanguageDef> = LANGUAGES.reduce(
  (acc, def) => {
    acc[def.code] = def;
    return acc;
  },
  {} as Record<Lang, LanguageDef>,
);

/** Total lookup — `Lang` is a closed union, so there is no miss to guard. */
export function languageDef(lang: Lang): LanguageDef {
  return BY_CODE[lang];
}

/** BCP-47 tag for Intl, toLocaleDateString/TimeString and SpeechRecognition. */
export function localeTagFor(lang: Lang): string {
  return BY_CODE[lang].localeTag;
}

/** ISO-639-1 code — asset filenames, Whisper hints, short edge payloads. */
export function shortFor(lang: Lang): LangShort {
  return BY_CODE[lang].short;
}

/**
 * Qual asset de mídia (vídeo de splash, poster da landing) um idioma herda.
 *
 * Os arquivos existem só em pt/en/nl — gravar vídeo e arte em nove idiomas é
 * decisão de produto com custo, não de código. A regra de herança tem TRÊS
 * faixas e mora aqui porque estava escrita em DOIS lugares (SplashHero e
 * Landing), cada um com sua cópia. Com duas faixas a duplicação passava
 * despercebida; com três, ela vira a divergência de sempre — o francês vendo
 * vídeo em português numa tela e em inglês na outra.
 *
 *   pt/en/nl  → têm asset próprio.
 *   es/pl/bg  → herdam o PORTUGUÊS (decisão nº 13, já no ar).
 *   fr/it/de  → herdam o INGLÊS. Divergência deliberada da linha acima: o
 *               espanhol herdar português tem uma lógica de proximidade real
 *               (um espanhol acompanha um vídeo em português); um francês, um
 *               italiano ou um alemão, não. Entre abrir o app com um vídeo em
 *               português e um em inglês, o inglês é o que essas três
 *               audiências entendem.
 *
 * É decisão de PRODUTO: para trocar, é esta tabela e nada mais.
 */
const ASSET_LANG: Record<Lang, "pt" | "en" | "nl"> = {
  "PT-BR": "pt",
  EN: "en",
  NL: "nl",
  ES: "pt",
  FR: "en",
  IT: "en",
  DE: "en",
  PL: "pt",
  BG: "pt",
};

export function assetLangFor(lang: Lang): "pt" | "en" | "nl" {
  return ASSET_LANG[lang];
}

/** The code the edge functions expect ("pt-BR" for Portuguese, ISO-639-1 otherwise). */
export function edgeLocaleFor(lang: Lang): EdgeLocale {
  return BY_CODE[lang].edgeLocale;
}

/**
 * Validate an untrusted string as an app language: a localStorage value, a
 * user_profiles.language read, a query param. Returns null rather than a
 * default so callers stay explicit about what "unknown" means for them — the
 * provider's tiebreaker and its backfill need to tell those cases apart.
 */
export function normalizeLang(raw: string | null | undefined): Lang | null {
  if (!raw) return null;
  // Deriva de LANGUAGES em vez de repetir a união: era mais uma lista para
  // esquecer de atualizar, e a i18n do app já tem histórico disso.
  return LANGUAGES.some((def) => def.code === raw) ? (raw as Lang) : null;
}

/**
 * Best-effort match of browser language tags (navigator.languages) against the
 * languages we ship. Prefix match, first hit wins, so "pt-PT" and "en-GB" land
 * on the closest thing we have. Returns null when nothing matches — the caller
 * decides whether that means DEFAULT_LANG.
 */
export function detectLangFromTags(tags: readonly string[]): Lang | null {
  for (const tag of tags) {
    if (!tag) continue;
    const prefix = tag.toLowerCase().split("-")[0];
    const hit = LANGUAGES.find((def) => def.short === prefix);
    if (hit) return hit.code;
  }
  return null;
}

/** Values interpolated into a translated string. */
/**
 * O idioma do PRIMEIRO FRAME. Uma função só, porque agora DOIS lugares
 * precisam da mesma resposta e uma divergência entre eles é exatamente o
 * flash de idioma errado que o code-splitting tinha que evitar:
 *
 *   · src/main.tsx  — para saber QUAL dicionário baixar antes de renderizar;
 *   · LanguageProvider — para o estado inicial do React.
 *
 * Se o main baixar "PT-BR" e o provider decidir "EN", o primeiro frame pinta
 * chave crua. Enquanto for esta função nos dois lados, não pode divergir; há
 * teste travando os dois call sites.
 *
 * Ordem: escolha persistida no aparelho > idioma do navegador > DEFAULT_LANG.
 * Nada de rede — o primeiro frame nunca espera o servidor.
 */
export function resolveBootLang(): Lang {
  const guardado = readStoredLang();
  if (guardado) return guardado;
  if (typeof navigator === "undefined") return DEFAULT_LANG;
  const tags = (navigator.languages && navigator.languages.length
    ? navigator.languages
    : [navigator.language]).filter(Boolean);
  return detectLangFromTags(tags) ?? DEFAULT_LANG;
}

/** A escolha persistida no aparelho, já validada. `null` = nunca escolheu. */
export function readStoredLang(): Lang | null {
  if (typeof localStorage === "undefined") return null;
  return normalizeLang(localStorage.getItem("vyv-lang"));
}

export type TVars = Record<string, string | number>;

/**
 * Interpolate `{{token}}` placeholders.
 *
 * Unknown tokens are left as-is on purpose: a missing variable then shows up as
 * a visible `{{token}}` during QA instead of rendering the string "undefined" to
 * a user, which is what the old `.replace()` chains did when a call site and a
 * translation disagreed about a token's spelling.
 */
export function interpolate(template: string, vars: TVars): string {
  return template.replace(/\{\{(\w+)\}\}/g, (whole, token: string) =>
    Object.prototype.hasOwnProperty.call(vars, token) ? String(vars[token]) : whole,
  );
}

/**
 * Categoria plural CLDR de um número, no idioma dado.
 *
 * Fina de propósito: `Intl.PluralRules` já carrega as regras do CLDR em todo
 * runtime que o app toca (browser, jsdom nos testes, Deno nas edge functions).
 * Escrever a regra à mão seria reimplementar uma tabela que o runtime tem —
 * e o polonês é exatamente o caso em que a intuição erra: `few` cobre 2, 3 e 4
 * (e 22, 23, 24…), enquanto 5 a 21 caem em `many`.
 */
export function pluralCategoryFor(lang: Lang, count: number): Intl.LDMLPluralRule {
  return new Intl.PluralRules(localeTagFor(lang)).select(count);
}

/**
 * Categorias que um idioma PRECISA ter escritas para os contadores que o app
 * realmente exibe.
 *
 * Não é `resolvedOptions().pluralCategories` — essa lista inclui categorias
 * inalcançáveis na prática. Português e espanhol declaram `many`, mas ela só
 * aparece a partir de 1 000 000; exigir a chave forçaria o tradutor a escrever
 * uma string que nenhum usuário veria. A amostragem sobre inteiros 0–200 dá a
 * resposta útil: pt/en/nl/es/bg precisam de `one` + `other`; o polonês precisa
 * de `one` + `few` + `many`.
 *
 * `other` entra sempre porque é o alvo do fallback de `tp()`.
 */
export function requiredPluralCategories(lang: Lang): Intl.LDMLPluralRule[] {
  const rules = new Intl.PluralRules(localeTagFor(lang));
  const found = new Set<Intl.LDMLPluralRule>(["other"]);
  for (let n = 0; n <= 200; n++) found.add(rules.select(n));
  return [...found].sort();
}
